import { StrictMode } from 'react'
import { ApolloProvider } from '@apollo/client'
import { loadErrorMessages, loadDevMessages } from '@apollo/client/dev'
import { ThemeProvider } from '@emotion/react'
import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider as MuiThemeProvider, StyledEngineProvider } from '@mui/material/styles'
import { LicenseInfo } from '@mui/x-license'
import * as Sentry from '@sentry/react'
import { createRoot } from 'react-dom/client'

// Replaces apollo-link-sentry's excludeGraphQLFetch (removed: incompatible with Sentry v8)
const excludeGraphQLFetch = (breadcrumb, hint) => {
  if (breadcrumb.category === 'fetch' && hint?.input && String(hint.input).includes('/graphql')) {
    return null
  }
  return breadcrumb
}
import pkg from '../package.json'
import client from './client'
// import { LocaleProvider } from './LocaleContext'
import { ProfileContextProvider } from './contexts/ProfileContext'
import { UserContextProvider } from './contexts/UserContext/UserContextProvider'
import Router from './routes/Router'
import theme from './theme/theme'
import { NotificationProvider } from './utils/Context/NotificationContext'
import GlobalStyle from './utils/GlobalStyle'
import './localization.config'
window.Sentry = Sentry
const { version } = pkg
const materialLicenseKey = import.meta.env.VITE_MATERIAL_LICENSE_KEY

LicenseInfo.setLicenseKey(materialLicenseKey)

if (import.meta.env.NODE_ENV === 'development') {
  // Adds messages only in a dev environment
  loadDevMessages()
  loadErrorMessages()
}

const isAllowedHost = () => {
  const hostname = window.location.hostname
  return (
    /\.(m2-loesung\.de|m2-4you\.com)$/.test(hostname) ||
    hostname === 'm2-loesung.de' ||
    hostname === 'm2-4you.com' ||
    hostname === 'localhost'
  )
}

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: isAllowedHost(),
  beforeBreadcrumb: excludeGraphQLFetch,
  initialScope: scope => {
    scope.setTag('domain', window.location.hostname)
    return scope
  },
  beforeSend: event => {
    // Filter out "Error message not found" from aborted requests
    if (event.exception?.values?.some(value => value.value === 'Error message not found.')) {
      return null
    }

    // Filter out AbortErrors
    if (event.exception?.values?.some(value => value.type === 'AbortError')) {
      return null
    }

    return event
  },
  integrations: [
    Sentry.browserTracingIntegration({
      traceFetch: false,
      traceXHR: true
    }),
    Sentry.replayIntegration({
      // Additional SDK configuration goes in here, for example:
      maskAllText: true,
      blockAllMedia: false
    }),
    Sentry.extraErrorDataIntegration({
      // Limit of how deep the object serializer should go. Anything deeper than limit will
      // be replaced with standard Node.js REPL notation of [Object], [Array], [Function] or
      // a primitive value. Defaults to 3.
      depth: 3
    }),
    Sentry.httpClientIntegration()
  ],
  release: version,
  tracesSampleRate: 0.1,
  // This sets the sample rate to be 10%. You may want this to be 100% while
  // in development and sample at a lower rate in production
  replaysSessionSampleRate: 0.1,
  // If the entire session is not sampled, use the below sample rate to sample
  // sessions when an error occurs.
  replaysOnErrorSampleRate: 1.0,
  environment: import.meta.env.VITE_ENV,
  tracePropagationTargets: [/m2-loesung.de/, /localhost/]
})

// Handle Vite chunk load errors (e.g., after a new deployment invalidates old chunks).
window.addEventListener('vite:preloadError', () => {
  const lastReload = sessionStorage.getItem('chunk-reload')
  if (!lastReload || Date.now() - Number(lastReload) > 10000) {
    sessionStorage.setItem('chunk-reload', String(Date.now()))
    window.location.reload()
  }
})

const container = document.getElementById('root')
const root = createRoot(container)

root.render(
  <StrictMode>
    <StyledEngineProvider injectFirst>
      <MuiThemeProvider theme={theme}>
        <ThemeProvider theme={theme}>
          <ApolloProvider client={client}>
            <UserContextProvider>
              <NotificationProvider>
                <ProfileContextProvider>
                  <Router />
                  <CssBaseline />
                  <GlobalStyle />
                </ProfileContextProvider>
              </NotificationProvider>
            </UserContextProvider>
          </ApolloProvider>
        </ThemeProvider>
      </MuiThemeProvider>
    </StyledEngineProvider>
  </StrictMode>
)
